import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { gsap } from "gsap";

import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import {
    BackgroundEvents,
    EventsManager,
    FlowEvents,
    GameEvents,
    ReelsEvents,
    WinEvents,
} from "../../../abstracts/events/EventsManager";
import { AbstractFlowPresentation } from "../../../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { IBetWayWinData } from "../../../abstracts/types/interfaces";
import { getTotalFromArrayByPropertyName } from "../../../abstracts/utils/JSUtils";
import { playNextBackgroundMusic } from "../../../abstracts/utils/SoundUtils";
import { UgliestCatch } from "../../UgliestCatch";
import { UCBackgroundView } from "../../modules/Background/UCBackgroundView";
import UCReel from "../../modules/Reels/UCReel";
import { UCReelsView } from "../../modules/Reels/UCReelsView";
import { UCBigFishSymbol } from "../../modules/Reels/symbols/UCBigFishSymbol";
import { UCMysterySymbol } from "../../modules/Reels/symbols/UCMysterySymbol";
import { UCSymbol, isFish } from "../../modules/Reels/symbols/UCSymbol";
import { UCWildSymbol } from "../../modules/Reels/symbols/UCWildSymbol";
import { UCWinView } from "../../modules/Winnings/UCWinView";
import { UCSymbolNames } from "../../types/UCEnums";

export class UCNormalIdlePresentation extends AbstractFlowPresentation {
    private ucReels: UCReel[];
    private winView: UCWinView;
    private reelsView: UCReelsView;
    private backgroundView: UCBackgroundView;
    private idleCall: gsap.core.Tween;
    private restartIdleCall: gsap.core.Tween;

    constructor() {
        super(`UCNormalIdlePresentation`);
    }

    protected handleEvents(): void {
        super.handleEvents();

        EventsManager.on(FlowEvents.BET_STARTED, () => {
            this.killTimeline();

            this.winView.removeChild(this.winView.waysWinScreen);
            this.idleCall?.kill();
            this.restartIdleCall?.kill();
        });

        EventsManager.on(ReelsEvents.GET_REELS, (reels: UCReel[]) => {
            this.ucReels = reels;
        });

        EventsManager.on(ReelsEvents.GET_REELS_VIEW, (reelsView: UCReelsView) => {
            this.reelsView = reelsView;
        });

        EventsManager.on(
            BackgroundEvents.GET_BACKGROUND_VIEW,
            (backgroundView: UCBackgroundView) => {
                this.backgroundView = backgroundView;
            }
        );

        EventsManager.on(WinEvents.GET_WINNINGS_VIEW, (winView: UCWinView) => {
            this.winView = winView;
        });

        const disableIdleAnimations = () => {
            this.reelsView.disableIdle();
            this.backgroundView.disableIdle();
            this.restartIdleCall?.kill();
            if (this.model.data.nextMode === AbstractGameMode.NORMAL) {
                this.restartIdleCall = gsap.delayedCall(5, () => {
                    this.reelsView.enableIdle();
                    this.backgroundView.enableIdle();
                });
            }
        };

        EventsManager.on(GameEvents.SHOW_XBET_FRAME, () => {
            disableIdleAnimations();
        });

        EventsManager.on(GameEvents.HIDE_XBET_FRAME, () => {
            disableIdleAnimations();
        });
    }

    public start(flowStateController: FlowStateController) {
        super.start(flowStateController);

        this.finishPresentation();

        const isJurisdictionAllowed: boolean =
                UgliestCatch.apiPlugin.gameClientConfiguration.boostedBetAllowed,
            isFreeBetsActive: boolean = UgliestCatch.apiPlugin.freeBets.hasFreeBets();

        if (
            isJurisdictionAllowed &&
            !isFreeBetsActive &&
            !UgliestCatch.autoPlayPlugin.isAutoplayRound
        ) {
            EventsManager.trigger(GameEvents.SHOW_XBET_BUTTON);
            EventsManager.trigger(GameEvents.UNBLOCK_XBET_BUTTON);
        }

        if (this.model.data === this.model.initData) {
            EventsManager.trigger(GameEvents.ENABLE_IDLE_ANIMATIONS, isJurisdictionAllowed);
        } else {
            this.idleCall = gsap.delayedCall(3 + Math.random() * 3, () => {
                EventsManager.trigger(GameEvents.ENABLE_IDLE_ANIMATIONS, isJurisdictionAllowed);
            });
        }

        if (!this.model.data.isRestore) {
            if (
                this.model.data.accumulatedRoundWin > 0 &&
                !UgliestCatch.model.isWinBelowStake() &&
                this.model.data.mode === AbstractGameMode.NORMAL
            ) {
                this.generateWinningPresentation();
            }

            if (this.model.data.nextMode === AbstractGameMode.NORMAL) {
                playNextBackgroundMusic();
            }
        }

        if (this.model.data.nextMode === AbstractGameMode.NORMAL) {
            this.reelsView.enableIdle();
            this.backgroundView.enableIdle();
        }
    }

    private generateWinningPresentation() {
        const isWinBelowStake: boolean =
            UgliestCatch.apiPlugin.gameClientConfiguration.belowStakeWinRestriction &&
            this.model.data.accumulatedRoundWin <= this.model.data.playedBetValue;

        if (isWinBelowStake) {
            this.flowPresentationTimeline.resume();
            return;
        }

        this.flowPresentationTimeline = gsap.timeline({
            repeat: -1,
            delay: 1,
        });
        const winningsArray = UgliestCatch.model.finalWinningPosition;

        if (this.model.data.betWayWins.length > 1) {
            this.model.data.betWayWins.forEach((betWayData: IBetWayWinData) => {
                const wayTimeline = gsap.timeline({
                    smoothChildTiming: true,
                    autoRemoveChildren: false,
                });

                this.ucReels.forEach((reel, reelIndex) => {
                    const visibleSymbols: AbstractSymbol[] = reel.getAllVisibleStopSymbols();
                    const reelWinSymbols = reel.getWinningSymbols(winningsArray[reelIndex]);
                    const wayWinSymbols: AbstractSymbol[] = [];

                    reelWinSymbols.forEach((symbol) => {
                        const cond1 = symbol.name === betWayData.symbol;

                        const cond2 =
                            symbol instanceof UCMysterySymbol &&
                            this.model.data.evaluatedArea[reelIndex][symbol.positionInArray - 1] ===
                                betWayData.symbol &&
                            winningsArray[reelIndex][symbol.positionInArray - 1];

                        const cond3 =
                            symbol instanceof UCMysterySymbol &&
                            isFish(symbol.revealedSymbolName as UCSymbolNames) &&
                            betWayData.symbol === `M4` &&
                            winningsArray[reelIndex][symbol.positionInArray - 1];

                        const cond4 =
                            symbol.name === UCSymbolNames.WILD &&
                            UgliestCatch.model.finalWinningPosition[reelIndex][
                                symbol.positionInArray - 1
                            ] &&
                            betWayData.length >= reelIndex + 1;

                        const cond5 =
                            symbol instanceof UCWildSymbol &&
                            UgliestCatch.model.finalWinningPosition[reelIndex][
                                symbol.positionInArray - 1
                            ] &&
                            symbol.isGold &&
                            isFish(betWayData.symbol as UCSymbolNames);

                        const cond6 =
                            betWayData.symbol === `M4` && isFish(symbol.name as UCSymbolNames);

                        const cond7 =
                            symbol instanceof UCBigFishSymbol &&
                            symbol.revealedSymbolName === betWayData.symbol;

                        if (cond1 || cond2 || cond3 || cond4 || cond5 || cond6 || cond7) {
                            const symbolWinAnimation = symbol.getWinAnimation();
                            wayTimeline.add(symbolWinAnimation, 0);
                            wayWinSymbols.push(symbol as UCSymbol);
                        }
                    });

                    visibleSymbols.forEach((symbol) => {
                        if (!wayWinSymbols.includes(symbol as UCSymbol)) {
                            wayTimeline.add((symbol as UCSymbol).getNoWinAnimation(), 0);
                        }
                    });
                });

                wayTimeline.add(
                    this.winView.waysWinScreen.handleWaysWin(
                        [],
                        [betWayData.amount],
                        -1,
                        wayTimeline.totalDuration()
                    ),
                    0
                );

                this.flowPresentationTimeline.add(wayTimeline);
            });
        }

        if (this.model.data.betWayWins.length > 0) {
            const allWaysTimeline = gsap.timeline({});

            this.ucReels.forEach((reel, reelIndex) => {
                const winSymbols: AbstractSymbol[] = reel.getWinningSymbols(
                    winningsArray[reelIndex]
                );

                winSymbols.forEach((symbol) => {
                    allWaysTimeline.add(symbol.getWinAnimation(), 0);
                });

                const nonWinSymbols = reel.getNonWinningSymbols(winningsArray[reelIndex]);

                nonWinSymbols.forEach((symbol) => {
                    allWaysTimeline.add((symbol as UCSymbol).getNoWinAnimation(), 0);
                });
            });

            const totalWaysCount: number = getTotalFromArrayByPropertyName(
                this.model.data.betWayWins,
                `numberOfCombinations`
            );

            allWaysTimeline.add(
                this.winView.waysWinScreen.handleWaysWin(
                    [],
                    [this.model.data.accumulatedRoundWin],
                    totalWaysCount,
                    allWaysTimeline.totalDuration()
                ),
                0
            );

            this.flowPresentationTimeline.add(allWaysTimeline);
        }
    }
}
